import { Injectable } from '@angular/core';
import {
  KindleFrictionId,
  ListeningTimeId,
  VisionCareId,
  VoicePreferenceId,
} from 'funnel/src/app/data/quiz';
import {
  formatList,
  getAnswerIds,
  getFirstAnswerId,
  getMomentumScore,
  hasEyeComfortConcern,
  ListPhrases,
  QuizAnswers,
  ResultMessages,
} from 'funnel/src/app/data/result-helpers';
import { RomanceSubgenreId } from '../data/romance-books';

declare const $localize: any;

export interface RomanceResultMessageParams {
  answers: QuizAnswers;
  subgenreIds: readonly RomanceSubgenreId[];
}

type RomanceSpiceLevelId = 'sweet' | 'warm' | 'steamy' | 'noLimits';
type RomanceBooksPerMonthId = 'oneOrFewer' | 'twoToThree' | 'fourToSix' | 'lostCount';

// books/year = mid-tier minutes a day × 365, divided by a typical 10-11 hour
// audiobook (the length of most romance/romantasy titles this audience picks).
interface RomanceListeningPlan {
  dailyLabel: string;
  booksPerYearMin: number;
  booksPerYearMax: number;
  potentialLift: number;
}

const romanceListPhrases: ListPhrases = {
  two: (first, second) =>
    $localize`:@@romanceResultTwoItemListV2:${first}:FIRST: and ${second}:SECOND:`,
  many: (items, last) =>
    $localize`:@@romanceResultMultiItemListV2:${items}:ITEMS:, and ${last}:LAST:`,
};

@Injectable({
  providedIn: 'root',
})
export class RomanceResultMessageService {
  // Same order as KindleResultMessageService.frictionPriority: whichever of
  // these was ticked drives the diagnosis sentence.
  private readonly frictionPriority: KindleFrictionId[] = [
    'noQuietTime',
    'wantHandsFree',
    'longChapters',
    'fallAsleep',
    'voicesMatter',
  ];

  // Short noun phrases that slot into "Your biggest blockers are {list}."
  private readonly frictionTextMap: Record<KindleFrictionId, string> = {
    noQuietTime: $localize`:@@romanceResultFrictionQuietV2:not enough quiet time`,
    fallAsleep: $localize`:@@romanceResultFrictionSleepV2:falling asleep mid-chapter`,
    longChapters: $localize`:@@romanceResultFrictionChaptersV2:chapters that feel too long to start`,
    wantHandsFree: $localize`:@@romanceResultFrictionHandsFreeV2:wanting to stay hands-free`,
    voicesMatter: $localize`:@@romanceResultFrictionVoicesV2:narration that never sounded right`,
  };

  private readonly visionCareTextMap: Partial<Record<VisionCareId, string>> = {
    gettingWorse: $localize`:@@romanceResultVisionGettingWorse:eye strain that keeps getting worse`,
    aBit: $localize`:@@romanceResultVisionABit:occasional eye strain`,
  };

  // getFirstAnswerId only uses this to validate the answer is a real
  // VisionCareId - the text lives in visionCareTextMap above.
  private readonly visionCareAllowedValues: Record<VisionCareId, true> = {
    gettingWorse: true,
    aBit: true,
    allGood: true,
  };

  // The romance funnel's time step also offers 'chores' and
  // 'workingOrStudying', which sit outside ConsumptionTimeId - keep the map
  // stringly-keyed so those answers are voiced too instead of dropped.
  private readonly windowTextMap: Record<string, string> = {
    inMorning: $localize`:@@romanceResultWindowMorningV2:your morning coffee`,
    workingOut: $localize`:@@romanceResultWindowWorkoutV2:walks and workouts`,
    onCommute: $localize`:@@romanceResultWindowCommuteV2:your commute`,
    takingBreak: $localize`:@@romanceResultWindowBreakV2:quick breaks`,
    chores: $localize`:@@romanceResultWindowChoresV2:chores around the house`,
    workingOrStudying: $localize`:@@romanceResultWindowWorkV2:work or study sessions`,
  };

  private readonly spiceTextMap: Record<RomanceSpiceLevelId, string> = {
    sweet: $localize`:@@romanceResultSpiceSweetV2:sweet`,
    warm: $localize`:@@romanceResultSpiceWarmV2:warm`,
    steamy: $localize`:@@romanceResultSpiceSteamyV2:steamy`,
    noLimits: $localize`:@@romanceResultSpiceNoLimitsV2:no-limits`,
  };

  private readonly subgenreTextMap: Partial<Record<RomanceSubgenreId, string>> = {
    contemporaryRomance: $localize`:@@romanceResultSubgenreContemporaryV2:modern love stories`,
    romantasy: $localize`:@@romanceResultSubgenreRomantasyV2:fantasy romance`,
    darkRomance: $localize`:@@romanceResultSubgenreDarkV2:dark romance`,
    historicalRomance: $localize`:@@romanceResultSubgenreHistoricalV2:historical romance`,
    smallTownRomance: $localize`:@@romanceResultSubgenreSmallTownV2:small-town romance`,
  };

  private readonly voicePreferenceTextMap: Record<VoicePreferenceId, string> = {
    friendlyFemale: $localize`:@@romanceResultVoicePrefFemale:starting you with a friendly female narrator`,
    calmMale: $localize`:@@romanceResultVoicePrefMale:starting you with a calm male narrator`,
    balancedNeutral: $localize`:@@romanceResultVoicePrefNeutral:keeping the narration balanced and neutral`,
    expressiveStoryteller: $localize`:@@romanceResultVoicePrefExpressive:leaning into an expressive storyteller`,
    exploreOptions: $localize`:@@romanceResultVoicePrefExplore:letting you explore the full voice library first`,
  };

  private readonly listeningPlanMap: Record<ListeningTimeId, RomanceListeningPlan> = {
    minutes0515: {
      dailyLabel: $localize`:@@romanceResultPlan0515V2:5-15 minutes a day`,
      booksPerYearMin: 5,
      booksPerYearMax: 6,
      potentialLift: 24,
    },
    minutes1530: {
      dailyLabel: $localize`:@@romanceResultPlan1530V2:15-30 minutes a day`,
      booksPerYearMin: 12,
      booksPerYearMax: 14,
      potentialLift: 29,
    },
    minutes3060: {
      dailyLabel: $localize`:@@romanceResultPlan3060V2:30-60 minutes a day`,
      booksPerYearMin: 24,
      booksPerYearMax: 27,
      potentialLift: 34,
    },
    hours123: {
      dailyLabel: $localize`:@@romanceResultPlan123V2:1-2 hours a day`,
      booksPerYearMin: 50,
      booksPerYearMax: 55,
      potentialLift: 38,
    },
    hours2plus: {
      dailyLabel: $localize`:@@romanceResultPlan2plusV2:2+ hours a day`,
      booksPerYearMin: 80,
      booksPerYearMax: 90,
      potentialLift: 40,
    },
  };

  // lostCount has no honest yearly number, so getYearlyBooksSentence voices
  // its phrase without one.
  private readonly booksPerMonthYearlyMap: Record<
    RomanceBooksPerMonthId,
    { phrase: string; yearly?: number }
  > = {
    oneOrFewer: {
      phrase: $localize`:@@romanceResultBooksPhraseOne:about a book a month`,
      yearly: 12,
    },
    twoToThree: {
      phrase: $localize`:@@romanceResultBooksPhraseTwoThree:2 to 3 books a month`,
      yearly: 30,
    },
    fourToSix: {
      phrase: $localize`:@@romanceResultBooksPhraseFourSix:4 to 6 books a month`,
      yearly: 60,
    },
    lostCount: {
      phrase: $localize`:@@romanceResultBooksPhraseLostCount:more books than you can count`,
    },
  };

  generateMessages(params: RomanceResultMessageParams): ResultMessages {
    const frictionIds = getAnswerIds<KindleFrictionId>(
      params.answers,
      'kindleFriction',
      this.frictionTextMap
    );
    const visionCareId = getFirstAnswerId<VisionCareId>(
      params.answers,
      'visionCare',
      this.visionCareAllowedValues
    );
    const listeningTimeId = getFirstAnswerId<ListeningTimeId>(
      params.answers,
      'listeningTime',
      this.listeningPlanMap
    );
    const windowIds = getAnswerIds<string>(params.answers, 'time', this.windowTextMap);
    const spiceLevelId = getFirstAnswerId<RomanceSpiceLevelId>(
      params.answers,
      'romanceSpiceLevel',
      this.spiceTextMap
    );
    const booksPerMonthId = getFirstAnswerId<RomanceBooksPerMonthId>(
      params.answers,
      'romanceBooksPerMonth',
      this.booksPerMonthYearlyMap
    );
    const voicePreferenceId = getFirstAnswerId<VoicePreferenceId>(
      params.answers,
      'romanceVoicePreference',
      this.voicePreferenceTextMap
    );

    const plan =
      (listeningTimeId && this.listeningPlanMap[listeningTimeId]) ||
      this.listeningPlanMap.minutes1530;
    const diagnosis = this.getDiagnosis(frictionIds, visionCareId);
    const frictionText = this.getFrictionText(frictionIds, visionCareId);
    const windowText = this.getWindowText(windowIds);
    const spiceText = this.getSpiceText(spiceLevelId);
    const subgenreText = this.getSubgenreText(params.subgenreIds);
    const voicePreferenceText = this.getVoicePreferenceText(voicePreferenceId);
    // This funnel never asks the timeForReading/audioExperience questions the kindle score also
    // weighs, so there is nothing honest to weight them with.
    const metric = getMomentumScore({
      frictionCount: frictionIds.length,
      visionCareId,
      potentialLift: plan.potentialLift,
    });
    const timeframe = $localize`:@@romanceResultMetricTimeframeV2:3 months`;

    const messages = [
      {
        text: $localize`:@@romanceResultMsg1V2:Based on your answers, the pattern is clear: ${diagnosis}:DIAGNOSIS:. Your biggest blockers are ${frictionText}:FRICTION:.`,
      },
      {
        text:
          $localize`:@@romanceResultMsg2BaseV2:The best plan for you is ${plan.dailyLabel}:DAILY_LABEL:, mostly during ${windowText}:WINDOWS:.` +
          this.getYearlyBooksSentence(booksPerMonthId, plan),
      },
      {
        text: $localize`:@@romanceResultMsg3V2:For a ${spiceText}:SPICE: ${subgenreText}:SUBGENRE: listener, Peech should optimise around long-session voices, easy resume and pace control - ${voicePreferenceText}:VOICE_PREF:.`,
      },
      {
        text: $localize`:@@romanceResultMsg4V2:Your Story Momentum Score is ${metric.currentValue}:CURRENT_VALUE:% today. With this plan it can realistically reach ${metric.potentialValue}:POTENTIAL_VALUE:% in ${timeframe}:TIMEFRAME:, because it only uses time you already have.`,
      },
      {
        text: $localize`:@@romanceResultMsg5V2:Next up: your plan with voices for long sessions and samples matched to ${subgenreText}:SUBGENRE: - plus the discount we've reserved for you on the next screen.`,
      },
    ];

    return {
      messages,
      metricProperties: {
        metric_name: 'Story Momentum Score',
        metric_abbreviation: 'SMS',
        current_value: metric.currentValue,
        potential_value: metric.potentialValue,
        timeframe,
        friction_count: frictionIds.length,
        listening_time: listeningTimeId,
      },
    };
  }

  private getDiagnosis(
    frictionIds: KindleFrictionId[],
    visionCareId: VisionCareId | undefined
  ): string {
    const primaryFriction = this.frictionPriority.find(id => frictionIds.includes(id));

    if (!primaryFriction && hasEyeComfortConcern(visionCareId)) {
      return $localize`:@@romanceResultDiagnosisEyeComfort:this is an eye-comfort problem, not a motivation problem`;
    }

    if (primaryFriction === 'noQuietTime' || primaryFriction === 'wantHandsFree') {
      return $localize`:@@romanceResultDiagnosisAccess:this is a time-access problem, not a motivation problem`;
    }

    if (primaryFriction === 'fallAsleep') {
      return $localize`:@@romanceResultDiagnosisEnergy:this is an energy problem, not a discipline problem`;
    }

    if (primaryFriction === 'longChapters') {
      return $localize`:@@romanceResultDiagnosisRestart:this is a restart-friction problem, especially with longer chapters`;
    }

    if (primaryFriction === 'voicesMatter') {
      return $localize`:@@romanceResultDiagnosisQuality:this is a quality problem because a flat narrator breaks the spell`;
    }

    return $localize`:@@romanceResultDiagnosisDefault:your reading routine needs easier access to the books you already chose`;
  }

  private getFrictionText(
    frictionIds: KindleFrictionId[],
    visionCareId: VisionCareId | undefined
  ): string {
    const blockers = frictionIds.map(id => this.frictionTextMap[id]);

    if (hasEyeComfortConcern(visionCareId)) {
      blockers.push(this.visionCareTextMap[visionCareId] as string);
    }

    if (blockers.length === 0) {
      return $localize`:@@romanceResultFrictionDefaultV2:finding a quiet stretch to read`;
    }

    return formatList(blockers, romanceListPhrases);
  }

  private getWindowText(windowIds: string[]): string {
    if (windowIds.length === 0) {
      return $localize`:@@romanceResultWindowDefaultV2:the pockets of your day`;
    }

    return formatList(
      windowIds.map(id => this.windowTextMap[id]),
      romanceListPhrases
    );
  }

  private getSpiceText(spiceLevelId: RomanceSpiceLevelId | undefined): string {
    return spiceLevelId
      ? this.spiceTextMap[spiceLevelId]
      : $localize`:@@romanceResultSpiceDefaultV2:devoted`;
  }

  private getSubgenreText(subgenreIds: readonly RomanceSubgenreId[]): string {
    const firstId = subgenreIds.find(id => this.subgenreTextMap[id]);

    return (
      (firstId && this.subgenreTextMap[firstId]) ||
      $localize`:@@romanceResultSubgenreDefaultV2:romance`
    );
  }

  private getVoicePreferenceText(voicePreferenceId: VoicePreferenceId | undefined): string {
    return voicePreferenceId
      ? this.voicePreferenceTextMap[voicePreferenceId]
      : $localize`:@@romanceResultVoicePrefDefault:matching the voice to your subgenre`;
  }

  // A missing booksPerMonth answer skips the sentence rather than guess.
  private getYearlyBooksSentence(
    booksPerMonthId: RomanceBooksPerMonthId | undefined,
    plan: RomanceListeningPlan
  ): string {
    if (!booksPerMonthId) {
      return '';
    }

    const { phrase, yearly } = this.booksPerMonthYearlyMap[booksPerMonthId];

    if (yearly === undefined) {
      return $localize`:@@romanceResultMsg2LostCountV2: At ${phrase}:PHRASE:, this plan adds ${plan.booksPerYearMin}:PLAN_MIN: to ${plan.booksPerYearMax}:PLAN_MAX: more without a single quiet reading hour.`;
    }

    return $localize`:@@romanceResultMsg2YearlyV2: At ${phrase}:PHRASE: that's ${yearly}:YEARLY: a year - and this plan adds ${plan.booksPerYearMin}:PLAN_MIN: to ${plan.booksPerYearMax}:PLAN_MAX: more without a single quiet reading hour.`;
  }
}
